// Device/breakpoint detection — Svelte 5 port of the Next `useDeviceDetect` hook.
// A client-only singleton: call `deviceDetect.start()` once from the root layout
// (or the home page) inside onMount and run the returned cleanup in onDestroy.

const BREAKPOINTS = { MOBILE: 768, TABLET: 1024 };

// The ONE condition for "lay this out like a phone", written the same way in
// styles and in scripts: a narrow window, or a short one (a phone on its side).
// In a stylesheet:  @media (max-width: 640px), (max-height: 500px) { … }
// In a script:      deviceDetect.isCompact
// `isMobile` below is older and wider (under 768px, or any phone or tablet by
// name); it stays as it is because screens depend on it. New code should use
// isCompact for layout and isTouch for touch behaviour.
export const COMPACT_QUERY = '(max-width: 640px), (max-height: 500px)';
const MOBILE_DEVICES = [
	'Android',
	'webOS',
	'iPhone',
	'iPad',
	'iPod',
	'BlackBerry',
	'IEMobile',
	'Opera Mini'
];

class DeviceDetect {
	isMobile = $state(false);
	isTablet = $state(false);
	isDesktop = $state(true);
	isTouch = $state(false);
	/** Matches COMPACT_QUERY: the phone layout of the stylesheets. */
	isCompact = $state(false);
	screenWidth = $state(0);

	#started = false;

	#isMobileUA() {
		if (typeof navigator === 'undefined') return false;
		return new RegExp(MOBILE_DEVICES.join('|'), 'i').test(navigator.userAgent);
	}

	#hasTouch() {
		if (typeof window === 'undefined') return false;
		return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
	}

	#update = () => {
		const width = window.innerWidth;
		const ua = this.#isMobileUA();
		this.isMobile = width < BREAKPOINTS.MOBILE || ua;
		this.isTablet = width >= BREAKPOINTS.MOBILE && width < BREAKPOINTS.TABLET && !ua;
		this.isDesktop = width >= BREAKPOINTS.TABLET && !ua;
		this.isTouch = this.#hasTouch();
		this.isCompact = window.matchMedia?.(COMPACT_QUERY).matches ?? width <= 640;
		this.screenWidth = width;
	};

	/** Attach listeners (call from onMount). Returns a cleanup fn for onDestroy. */
	start() {
		if (typeof window === 'undefined' || this.#started) return () => {};
		this.#started = true;
		this.#update();

		/** @type {ReturnType<typeof setTimeout> | undefined} */
		let resizeTimeout;
		const onResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(this.#update, 150);
		};
		window.addEventListener('resize', onResize);
		window.addEventListener('orientationchange', this.#update);

		return () => {
			clearTimeout(resizeTimeout);
			window.removeEventListener('resize', onResize);
			window.removeEventListener('orientationchange', this.#update);
			this.#started = false;
		};
	}
}

export const deviceDetect = new DeviceDetect();
